import {useDispatch, useSelector} from "react-redux";
import {
    ConfigAddressApi,
    ConfigApi,
    DineInType,
    OrderSchedulingMode,
    OrderType,
    StoreConfigCoordinatesApi
} from "../Api/types";
import {updateLocationCheck, updateSession, updateSessionCoordinates, updateSessionLocation} from "./actions";
import {api} from "../Api/api";
import {useCart} from "../Cart/cartService";
import StorageStore from "../Common/Storage/storageStore";
import {SessionState} from "./types";
import {selectSession} from "./reducer";
import StoreUtils from "../Store/StoreUtils";
import CartUtils from "../Cart/CartUtils";
import {DateHelper} from "../Common/Date/DateHelper";
import {useRef} from "react";

interface SessionService {
    updateLocation: (location: ConfigAddressApi) => Promise<any>;
    updateCartData: (session: SessionState) => Promise<any>;
    updateCoordinates: (coordinates: StoreConfigCoordinatesApi) => Promise<void>;
    update: (session: SessionState, disableUpdateCart?: boolean) => Promise<SessionState>;
    init: (locale: string, config: ConfigApi) => Promise<SessionState>;
    getDefaultOrderType: (config: ConfigApi) => OrderType;
    getDefaultDineInType: (currentDineInType: DineInType | undefined, config: ConfigApi, defaultOrderType: OrderType) => DineInType | undefined;
}

const THIRTY_MINUTES_IN_MS = 30 * 60 * 1000;

export const useSessionService = (): SessionService => {
    const dispatch = useDispatch();
    const locationState = useSelector(selectSession);
    const cartService = useCart("location");
    const locationAbortController = useRef<AbortController | undefined>(undefined);

    const getDefaultDineInType = (currentDineInType: DineInType | undefined, config: ConfigApi, defaultOrderType: OrderType) => {
        if (currentDineInType === DineInType.COUNTER) {
            if (config.ordering.dine_in_counter) {
                return currentDineInType
            }
            return undefined;
        }
        if (currentDineInType === DineInType.TABLE) {
            if (config.ordering.dine_in_table) {
                return currentDineInType;
            }
            return undefined;
        }

        const dineInOptions = StoreUtils.getDineInTypes(config);

        if (defaultOrderType === OrderType.DINE_IN) {
            return dineInOptions[0];
        }
    }

    const getDefaultOrderType = (config: ConfigApi): OrderType => {
        const defaultOrderType = config.ordering.default_order_type;
        const orderTypes = StoreUtils.getOrderTypes(config);

        if (defaultOrderType) return defaultOrderType as OrderType;

        return orderTypes[0];
    };

    const init = async (locale: string, config: ConfigApi) => {
        const defaultOrderType = getDefaultOrderType(config);

        let sessionState = {
            pickup_at: locationState.pickup_at,
            table_number: locationState.table_number,
            room_number: locationState.room_number,
            scan_time: locationState.scan_time,
            cart_comment: locationState.cart_comment,
            order_type: defaultOrderType,
            dine_in_type: getDefaultDineInType(locationState.dine_in_type, config, defaultOrderType),
            context_time: locationState.context_time,
            context: locationState.context,
        } as SessionState

        const context = StorageStore.getItem("context");

        if (context) {
            sessionState.context = context;
        }

        const locationJson = StorageStore.getItem("location");
        if (locationJson) {
            let location = undefined;
            try {
                location = JSON.parse(locationJson) as ConfigAddressApi;
                if (location) {
                    sessionState.address = location;
                    // await update(location);
                }
            } catch (error) {
                StorageStore.removeItem("location")
            }
        }
        const coordinatesJson = StorageStore.getItem("coordinates");
        if (coordinatesJson) {
            let coordinates = undefined;
            try {
                coordinates = JSON.parse(coordinatesJson) as StoreConfigCoordinatesApi;
                if (coordinates) {
                    sessionState.coordinates = coordinates;
                }
            } catch (error) {
                StorageStore.removeItem("coordinates")
            }

        }
        const contactJson = StorageStore.getItem("contact");
        let contact = undefined;
        if (contactJson) {
            try {
                contact = JSON.parse(contactJson);

                if (contact) {
                    sessionState.contact = contact
                }
            } catch (error) {
                console.log("ERROR CONTACT")
                // StorageStore.removeItem("contact")
            }
        }

        const sessionCartJson = StorageStore.getItem("session_cart");
        const isMultistore = StoreUtils.isMultistore(config);

        if (sessionCartJson) {
            let sessionCart = undefined;
            try {
                sessionCart = JSON.parse(sessionCartJson);

                if (sessionCart) {
                    if (sessionCart.table_number) sessionState.table_number = sessionCart.table_number;
                    if (sessionCart.room_number) sessionState.room_number = sessionCart.room_number;
                    if (sessionCart.pickup_at) {
                        const orderSchedulingMode = StoreUtils.getOrderSchedulingMode(config);
                        const now = new Date();
                        const pickupAt = new Date(sessionCart.pickup_at as string);
                        if (now.getTime() < pickupAt.getTime()) {
                            sessionState.pickup_at = sessionCart.pickup_at;
                        }
                        if ((orderSchedulingMode === OrderSchedulingMode.SAME_DAY || orderSchedulingMode === OrderSchedulingMode.DISABLED) && !isMultistore) {
                            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                            const pickupDay = new Date(pickupAt.getFullYear(), pickupAt.getMonth(), pickupAt.getDate());

                            if (pickupDay.getTime() > today.getTime()) {
                                sessionState.pickup_at = undefined;
                            }
                        }
                    }
                    if (sessionCart.order_type && (StoreUtils.getOrderTypes(config).includes(sessionCart.order_type) || isMultistore)) sessionState.order_type = sessionCart.order_type;
                    if (sessionCart.dine_in_type && StoreUtils.getDineInTypes(config).includes(sessionCart.dine_in_type)) sessionState.dine_in_type = getDefaultDineInType(sessionCart.dine_in_type, config, sessionState.order_type as OrderType);
                    if (sessionCart.scan_time) sessionState.scan_time = sessionCart.scan_time;
                    if (sessionCart.comment) sessionState.cart_comment = sessionCart.comment;
                }
            } catch (error) {
                StorageStore.removeItem("session_cart")
            }
        }
        if (locale) {
            sessionState.locale = locale;
        }

        sessionState.default_delivery_price_money = config.ordering.delivery_price_money;
        sessionState.default_min_order_price_money = config.ordering.min_order_price_money;
        if (sessionState.scan_time) {
            const now = new Date();
            const scanTime = new Date(sessionState.scan_time);
            if (now.getTime() - scanTime.getTime() > THIRTY_MINUTES_IN_MS) {
                sessionState.scan_time = undefined;
                sessionState.table_number = undefined;
                sessionState.room_number = undefined;
            }
        }


        const contextTime = StorageStore.getItem("context_time");

        if (sessionState.context) {
            if (!contextTime) {
                sessionState.context_time = DateHelper.toString(new Date());
            } else {
                sessionState.context_time = contextTime;
            }
        }
        if (sessionState.context && sessionState.context_time) {
            const now = new Date();
            const contextTime = new Date(sessionState.context_time);

            if (now.getTime() - contextTime.getTime() > THIRTY_MINUTES_IN_MS) {
                sessionState.context = undefined;
                sessionState.context_time = undefined;
            } else {
                sessionState.context_time = DateHelper.toString(new Date());
            }
        } else {
            sessionState.context = undefined;
            sessionState.context_time = undefined;
        }

        await updateCache(sessionState);
        return sessionState;
    }
    const updateCache = async (session: SessionState) => {
        dispatch(updateSession(session));
        if (session.address) {
            StorageStore.setItem('location', JSON.stringify(session.address));
        } else {
            StorageStore.removeItem('location');
        }
        let sessionCart = {} as any;
        if (session.pickup_at) sessionCart.pickup_at = session.pickup_at;
        if (session.table_number) sessionCart.table_number = session.table_number;
        if (session.room_number) sessionCart.room_number = session.room_number;
        if (session.dine_in_type) sessionCart.dine_in_type = session.dine_in_type;
        if (session.order_type) sessionCart.order_type = session.order_type;
        if (session.scan_time) sessionCart.scan_time = session.scan_time;
        if (session.cart_comment) sessionCart.comment = session.cart_comment; else sessionCart.comment = undefined;

        if (session.order_type) {
            StorageStore.setItem('order_type', session.order_type);
        } else {
            StorageStore.removeItem('order_type');
        }

        if (session.context_time) {
            StorageStore.setItem('context_time', session.context_time);
        } else {
            StorageStore.removeItem('context_time');
        }
        if (session.context) {
            StorageStore.setItem('context', session.context);
        } else {
            StorageStore.removeItem('context');
        }

        if (Object.keys(sessionCart).length > 0) {
            StorageStore.setItem('session_cart', JSON.stringify(sessionCart));
        }

        // if(session.contact){
        //     StorageStore.setItem('contact', JSON.stringify(session.contact));
        // }
    }
    const updateCartData = async (newSessionData: SessionState) => {
        let newSession = {...locationState}
        if (newSessionData.contact) {
            newSession.contact = newSessionData.contact
        }
        if (newSessionData.cart_comment) {
            newSession.cart_comment = newSessionData.cart_comment
        }
        await updateCache(newSession)
    }
    const update = async (session: SessionState, disableUpdateCart?: boolean) => {
        if (locationState.room_number !== session.room_number || locationState.table_number !== session.table_number) {
            session.scan_time = DateHelper.toString(new Date());
        }

        if (locationState.context !== session.context) {
            session.context_time = DateHelper.toString(new Date());
        }

        await updateCache(session);
        let locationCheck = await api.getLocationCheck(session.address);
        dispatch(updateLocationCheck(locationCheck));
        if (disableUpdateCart) {
            return session;
        }
        try {
            let data = {
                pickup_at: session.pickup_at,
                type: session.order_type,
                number: CartUtils.getCartNumber(session),
                address: session.address
            }
            await cartService.update(data, true);
            return session;
        } catch (err) {
            console.log(err)
        }
        return session;
    }
    const updateLocation = async (newLocation: ConfigAddressApi) => {
        if (locationState.address.street == newLocation?.street
            && locationState.address.city == newLocation?.city
            && locationState.address.zip_code == newLocation?.zip_code
            && locationState.address.build_nr == newLocation?.build_nr
            && locationState.address.flat_nr == newLocation?.flat_nr
            && locationState.address.country == newLocation?.country
            && !locationState.address_dirty
        ) {
            return {
                address: newLocation,
                coordinates: locationState.coordinates
            };
        }
        dispatch(updateSessionLocation(newLocation));
        StorageStore.setItem('location', JSON.stringify(newLocation));

        if (locationAbortController.current) {
            locationAbortController.current.abort();
        }
        locationAbortController.current = new AbortController();
        let locationCheck = await api.getLocationCheck(newLocation, locationAbortController.current);
        dispatch(updateLocationCheck(locationCheck));
        StorageStore.setItem('coordinates', JSON.stringify(locationCheck.coordinates));
        try {
            let data = {
                pickup_at: locationState.pickup_at,
                type: locationState.order_type,
                room_number: locationState.room_number,
                table_number: locationState.table_number,
                address: newLocation
            }
            await cartService.update(data);
        } catch (err) {
            console.log(err)
        }
        return {
            address: newLocation,
            coordinates: locationCheck.coordinates
        };
    }

    const updateCoordinates = async (newCoordinates: StoreConfigCoordinatesApi) => {
        dispatch(updateSessionCoordinates(newCoordinates));
        StorageStore.setItem('coordinates', JSON.stringify(newCoordinates));
    }
    return {
        updateLocation,
        updateCartData,
        updateCoordinates,
        update,
        init,
        getDefaultOrderType,
        getDefaultDineInType
    };
};
